// Renderer bridge from MSSQL procedural completion to the T-SQL parse worker
// (plan16 T2). The two MSSQL emit helpers (`mssqlProceduralLocals.ts`,
// `mssqlBatchLocals.ts`) used to run a full-buffer, uncached ANTLR parse on the
// renderer main thread per completion request (measured ~5 s warm / ~12 s cold
// on the 67 KB reference fixture). This module routes them through the already-
// built parse worker (main-thread block ~0.5-1.1 ms) with a strict fallback
// ladder so a keystroke NEVER blocks and NEVER throws.
//
// Small-source inline routing (Plan 15 Gate D): sources at or under
// `SMALL_SOURCE_INLINE_BYTES` never enter the ladder below — they resolve
// inline directly (millisecond-scale parse), so a keystroke on a small buffer
// can never lose its locals to the worker's cold-load deadline or to the
// single-flight busy-drop. See the constant's doc for the regression this
// closes.
//
// Fallback ladder (D4), for sources ABOVE the small-source threshold:
//   worker reply            → the worker-side scope (locals + routine params).
//   worker CONSTRUCTION     → parse inline ONCE, then latch inline-only for the
//   failure OR reply ERROR    session (no respawn storms) + fire-and-forget
//   OR script `error` event   telemetry.
//   reply DEADLINE           → empty locals for THIS request; the worker stays
//   (cold-parse cover)        for the next request (no timer blocks the caret).
//   reply `oversize` (D6)    → empty locals, NO inline fallback (the buffer is
//                             too large to parse on the main thread either).
//
// Single-flight coalescing (D6's in-flight dedup, worker half): at most one
// scope request is ever outstanding — same-key callers attach to it, other
// keys answer empty for that keystroke. See the `inFlight` doc block for the
// measured built-app failure this prevents.
//
// The worker path (D1) resolves scope INSIDE the worker where the ANTLR
// `parseTree` is still alive, and returns plain candidate data (names / types /
// kinds only) — no ranges cross the IPC boundary, so neither the parseTree-
// stripping trap nor the slice-relative-coordinate trap can bite. This module
// only maps that plain data (or the inline-fallback equivalent) back into the
// scope shape the emit helpers consume; the emit helpers own the tier / detail
// candidate mapping so their output stays byte-identical to the pre-worker code.

import {
  isWorkerRespawnError,
  requestProceduralScope,
  respawnParseWorker,
  type WorkerRoutineParam,
  type WorkerScopeLocal,
} from '@/features/sql-grammar-tsql/parseWorkerClient';
import type { ScopeWorkerReply } from '@/features/sql-grammar-tsql/parseWorkerClient';
import { loadCompletionModule, noteCompletionIoWait } from '@/features/intellisense/telemetry/providerCost';
import {
  MAX_MAIN_THREAD_SLICE_BYTES,
  SMALL_SOURCE_INLINE_BYTES,
} from './proceduralParseLimits';

export { MAX_MAIN_THREAD_SLICE_BYTES, SMALL_SOURCE_INLINE_BYTES };

/** Normalized procedural scope consumed by the MSSQL emit helpers. Shared
 *  shape across BOTH the worker path and the inline-fallback path so the emit
 *  helpers map candidates exactly once, identically for either source. */
export interface ProceduralScope {
  readonly locals: readonly WorkerScopeLocal[];
  readonly routineParams: readonly WorkerRoutineParam[];
}

/** Deadline for a worker scope reply (D4). Generous enough to cover a cold
 *  ANTLR-bundle load; beyond it we return empty locals for THIS request but
 *  keep the worker alive for the next one. NEVER a timer that blocks the
 *  keystroke — the other candidate families always flow regardless. */
export const WORKER_SCOPE_DEADLINE_MS = 1_500;

/** Abandon an in-flight worker job older than this and issue a fresh request.
 *  Guards single-flight coalescing (below) against a genuinely hung worker
 *  pinning the slot forever. Sized well above the worst measured cold
 *  full-file parse in the packaged app (17.2 s for the 67 KB perf fixture). */
export const INFLIGHT_HARD_CAP_MS = 60_000;

const EMPTY: ProceduralScope = { locals: [], routineParams: [] };

/** Rejection sentinel distinguishing a deadline (retry the worker next time)
 *  from a real worker construction/reply error (latch to inline-only). */
const DEADLINE = Symbol('procedural-scope-deadline');

/** Once the worker fails to construct or replies with an error, we stop trying
 *  it for the rest of the session and serve every request inline. This avoids
 *  a respawn storm when the worker/bundle is genuinely unavailable (D4). */
let inlineOnlyLatched = false;

/** An inline parse has resolved, so the T-SQL parser classes are loaded
 *  (provider-cost measurement — see `inlineScope`). */
let inlineParserReady = false;

/** Single-flight coalescing (the worker-path half of D6's in-flight dedup).
 *
 *  THE defect this kills (found in the built-app e2e, plan16 T2): the worker
 *  is single-threaded and a cold full-file parse of a large buffer takes many
 *  seconds (measured 17.2 s for the 67 KB fixture in the packaged app). Every
 *  completion retrigger used to post ANOTHER scope request; deadlined callers
 *  abandoned their promise but the worker still ground each duplicate job, so
 *  the queue never drained and NO request ever completed inside its own
 *  deadline — zero worker replies for an entire session on big files.
 *
 *  Rules: at most ONE scope request is outstanding. A call with the SAME
 *  source+offset attaches to it (own fresh deadline — so a caller arriving
 *  late in a long parse gets the reply the moment it lands). A call with a
 *  DIFFERENT key returns empty for this keystroke instead of queueing (the
 *  worker's own side-table makes the follow-up request cheap). A slot older
 *  than `INFLIGHT_HARD_CAP_MS` is abandoned. */
let inFlight: {
  readonly source: string;
  readonly offset: number;
  readonly promise: Promise<ScopeWorkerReply>;
  readonly startedAt: number;
} | null = null;

interface ParseWorkerStats {
  /** Counts CALLER-resolutions served by a worker reply, not distinct worker
   *  messages — coalesced callers attached to one in-flight job each increment
   *  this once when the shared reply lands (plan16 T2 review F5). */
  workerReplies: number;
  inlineFallbacks: number;
  /** Requests served inline because the source is at or under
   *  `SMALL_SOURCE_INLINE_BYTES` (Plan 15 Gate D). Deliberately SEPARATE from
   *  `inlineFallbacks` — the built-app e2e proves the worker path via
   *  `workerReplies > 0 && inlineFallbacks === 0` on a large fixture, and the
   *  small-source route must not muddy that signal. */
  smallSourceInline: number;
  deadlines: number;
  oversize: number;
  latched: boolean;
  /** Count of non-deadline worker failures (construction throw, error reply,
   *  worker script `error` event, messageerror). */
  workerErrors: number;
  /** Message of the most recent worker failure — the built-app diagnostic for
   *  "why did the bridge latch to inline" (plan16 T2 review I1). */
  lastWorkerError: string | null;
  /** Requests answered empty because a DIFFERENT-key worker job was already in
   *  flight (single-flight coalescing kept the worker queue bounded). */
  busyDrops: number;
  /** Times the hard cap expired and the worker was terminated + left for lazy
   *  respawn (F2 — a genuinely hung worker no longer wedges the session). */
  hardCapRespawns: number;
}

/** Always-on diagnostic counters (no PII, low cardinality). Exposed on
 *  `globalThis.__jamSqlParseWorkerStats__` so the real-built-app e2e can prove
 *  the WORKER path (not the inline fallback) produced the candidates. Mutated
 *  in place so the reference the e2e captures stays live. */
const stats: ParseWorkerStats = {
  workerReplies: 0,
  inlineFallbacks: 0,
  smallSourceInline: 0,
  deadlines: 0,
  oversize: 0,
  latched: false,
  workerErrors: 0,
  lastWorkerError: null,
  busyDrops: 0,
  hardCapRespawns: 0,
};
(globalThis as unknown as { __jamSqlParseWorkerStats__?: ParseWorkerStats }).__jamSqlParseWorkerStats__ =
  stats;

/** Test seam — reset the latch + stats between cases. Keeps the SAME `stats`
 *  object reference on `globalThis` valid across resets. */
export function __resetProceduralWorkerBridgeForTest(): void {
  inlineOnlyLatched = false;
  inlineParserReady = false;
  inFlight = null;
  stats.workerReplies = 0;
  stats.inlineFallbacks = 0;
  stats.smallSourceInline = 0;
  stats.deadlines = 0;
  stats.oversize = 0;
  stats.latched = false;
  stats.workerErrors = 0;
  stats.lastWorkerError = null;
  stats.busyDrops = 0;
  stats.hardCapRespawns = 0;
}

/**
 * Resolve the procedural scope (locals + routine params) visible at `offset`
 * via the parse worker, degrading through the fallback ladder above. NEVER
 * throws — the worst case is an empty scope, which the emit helpers surface as
 * "no procedural locals for this request".
 */
export async function getProceduralScope(
  source: string,
  offset: number,
): Promise<ProceduralScope> {
  if (inlineOnlyLatched) return inlineFallbackUse(source, offset);

  // Small-source inline routing (Plan 15 Gate D): a sub-threshold buffer's
  // parse is millisecond-scale, so it never pays the worker route's failure
  // modes (cold-load deadline → empty locals; single-flight busy-drop →
  // empty locals). This restores the pre-plan16-T2 inline behavior for the
  // buffer class where it was always cheap.
  if (source.length <= SMALL_SOURCE_INLINE_BYTES) {
    stats.smallSourceInline += 1;
    return inlineScope(source, offset);
  }

  // Single-flight coalescing — see the `inFlight` doc block above.
  if (inFlight && Date.now() - inFlight.startedAt > INFLIGHT_HARD_CAP_MS) {
    // F2: a job pinned past the hard cap (sized well above the worst measured
    // cold parse) means the single worker thread is almost certainly hung —
    // terminate + clear it so the request below spawns a FRESH worker instead
    // of queueing behind the wedge forever. The respawn rejects the slot's
    // promise; no live caller sees that rejection (everyone attached to it
    // deadlined out at WORKER_SCOPE_DEADLINE_MS long ago, and the slot's own
    // swallow-catch handles cleanup), so the inline-only latch is NOT tripped.
    stats.hardCapRespawns += 1;
    respawnParseWorker('scope request exceeded INFLIGHT_HARD_CAP_MS');
    inFlight = null;
  }
  if (inFlight && (inFlight.source !== source || inFlight.offset !== offset)) {
    stats.busyDrops += 1;
    return EMPTY;
  }

  let reply: ScopeWorkerReply;
  try {
    let attach: Promise<ScopeWorkerReply>;
    if (inFlight) {
      attach = inFlight.promise;
    } else {
      const p = requestProceduralScope({ source, caretOffset: offset });
      inFlight = { source, offset, promise: p, startedAt: Date.now() };
      void p
        .catch(() => {})
        .finally(() => {
          if (inFlight?.promise === p) inFlight = null;
        });
      attach = p;
    }
    // A worker round-trip (new or joined in flight) — real I/O for the
    // provider-cost measurement. The inline paths note only a cold parser
    // load (`loadCompletionModule`); the busy-drop above returns immediately.
    noteCompletionIoWait();
    reply = await withDeadline(attach, WORKER_SCOPE_DEADLINE_MS);
  } catch (err) {
    if (err === DEADLINE) {
      // Deadline — empty locals for THIS request; the worker survives for the
      // next one (do NOT latch: a slow cold-load reply is not a failure).
      stats.deadlines += 1;
      return EMPTY;
    }
    if (isWorkerRespawnError(err)) {
      // A hard-cap respawn (F2, fired here or by the syntax path) rejected the
      // in-flight request. That is a deliberate reset of a hung worker, not
      // evidence the worker/bundle is unavailable — treat like a deadline
      // (empty for THIS request, no inline-only latch) so the freshly-spawned
      // worker gets to serve the next keystroke.
      stats.deadlines += 1;
      return EMPTY;
    }
    // Worker construction failure, error reply, or worker script `error`
    // event → latch inline-only + fire one-shot telemetry, then serve this
    // (and every future) request inline. Record the failure for the built-app
    // stats hook (review I1) — this is how a field failure names itself.
    stats.workerErrors += 1;
    stats.lastWorkerError = err instanceof Error ? err.message : String(err);
    if (!inlineOnlyLatched) {
      inlineOnlyLatched = true;
      stats.latched = true;
      fireFallbackTelemetry();
    }
    return inlineFallbackUse(source, offset);
  }

  if (reply.oversize) {
    // D6: buffer too large for the worker; the inline path would be even worse,
    // so we do NOT fall back — just skip procedural locals for the request.
    stats.oversize += 1;
    return EMPTY;
  }
  stats.workerReplies += 1;
  // `?? []` keeps a partially-malformed reply from throwing on the keystroke
  // (degradation-safe). A field-mapping mistake still surfaces: dropped/swapped
  // arrays make the expected locals ABSENT, which the real-DB e2e catches.
  return { locals: reply.locals ?? [], routineParams: reply.routineParams ?? [] };
}

/**
 * F3 cold-start warm-up seam (codex P1 fix). Start the SAME single-flight worker
 * scope request the completion path uses, EARLY (at editor mount), and register
 * it in `inFlight` — so a completion for the same buffer that arrives during the
 * ~17 s cold parse ATTACHES to this in-flight job (same key) or is busy-dropped
 * (different caret) instead of posting a SECOND scope request. The single-
 * threaded worker (which has no concurrent-full-reparse mutex) then parses the
 * large buffer ONCE, not twice — closing the finding where a raw warm-up request
 * was invisible to `inFlight`, so completions stacked a duplicate full parse and
 * EXTENDED the cold period rather than shortening it.
 *
 * Fire-and-forget, and deliberately NOT a `getProceduralScope` call:
 *   • it imposes NO deadline — warm-up must never race the keystroke clock; the
 *     completion path keeps its own `WORKER_SCOPE_DEADLINE_MS` when it attaches;
 *   • it consumes NO reply and mutates NONE of the completion-path stats
 *     (`workerReplies` / `deadlines` / `busyDrops` / … stay owned by real
 *     keystrokes) — the keystroke path is untouched;
 *   • the reply, whenever it lands or rejects, only settles the in-flight slot.
 *
 * The caller (`proceduralWarmUp`) owns the gating — `looksLikeProceduralOpener`
 * plus the once-per-(documentUri, worker-generation) latch — so this seeds at
 * most once per warm-up and only for procedural openers. It unconditionally
 * (re)seeds `inFlight`: the caller's latch already guarantees freshness, and on
 * a worker respawn the prior slot's promise is rejected and self-clears without
 * clobbering the new one (the `finally` below checks identity).
 */
export function primeProceduralScopeWarmUp(source: string, caretOffset: number): void {
  // Worker already known-unavailable this session — completions serve inline and
  // never read `inFlight` (see the top of `getProceduralScope`), so warming it
  // would seed a slot nobody reads. Nothing to do.
  if (inlineOnlyLatched) return;
  const p = requestProceduralScope({ source, caretOffset });
  inFlight = { source, offset: caretOffset, promise: p, startedAt: Date.now() };
  void p
    .catch(() => {})
    .finally(() => {
      if (inFlight?.promise === p) inFlight = null;
    });
}

async function inlineFallbackUse(
  source: string,
  offset: number,
): Promise<ProceduralScope> {
  stats.inlineFallbacks += 1;
  return inlineScope(source, offset);
}

/** Race a promise against a deadline. On timeout the returned promise rejects
 *  with the `DEADLINE` sentinel; the underlying request is abandoned (its
 *  one-shot listener self-removes if the worker ever replies). */
function withDeadline<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(DEADLINE), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

function fireFallbackTelemetry(): void {
  const api = (
    globalThis as unknown as {
      electronAPI?: {
        analytics?: {
          track?: (name: string, props?: Record<string, unknown>) => Promise<unknown>;
        };
      };
    }
  ).electronAPI;
  api?.analytics
    ?.track?.('feature:intellisense_worker_fallback', { engine: 'mssql' })
    ?.catch(() => {});
}

// ─── Inline fallback (main-thread ANTLR parse) ──────────────────────────────
//
// The rare path taken only when the worker is unavailable. Reuses the exact
// parse + scope + signature-extraction sequence the pre-worker emit helper ran
// inline, extracted here so both emit helpers share one degrade path and the
// worker path and the inline path produce the identical `ProceduralScope`.

/**
 * Inline scope resolution on the main thread. Guarded by
 * `MAX_MAIN_THREAD_SLICE_BYTES` (D6) and wrapped in try/catch so a not-yet-
 * loaded bundle or a parse failure degrades to an empty scope.
 */
async function inlineScope(source: string, offset: number): Promise<ProceduralScope> {
  if (source.length > MAX_MAIN_THREAD_SLICE_BYTES) return EMPTY;
  try {
    const { parseProceduralBody } = await loadCompletionModule(
      '@/features/sql-grammar-tsql/parse',
      () => import('@/features/sql-grammar-tsql/parse'),
    );
    const { resolveScopeTSql } = await loadCompletionModule(
      '@/features/sql-grammar-tsql/resolver/resolveScopeTSql',
      () => import('@/features/sql-grammar-tsql/resolver/resolveScopeTSql'),
    );
    const { extractProcedureSignatures } = await loadCompletionModule(
      '@/features/sql-grammar-tsql/extractors/procedureSignatures',
      () => import('@/features/sql-grammar-tsql/extractors/procedureSignatures'),
    );
    const { extractFunctionSignatures } = await loadCompletionModule(
      '@/features/sql-grammar-tsql/extractors/functionSignatures',
      () => import('@/features/sql-grammar-tsql/extractors/functionSignatures'),
    );

    // Until one inline parse has resolved, `parseProceduralBody` may wait on
    // its parser classes' first load — a real-I/O wait for the provider-cost
    // measurement; afterwards it parses in the calling task.
    if (!inlineParserReady) noteCompletionIoWait();
    const parsed = await parseProceduralBody(source, { engine: 'mssql' });
    inlineParserReady = true;
    const scope = resolveScopeTSql(parsed, offset);

    const locals: WorkerScopeLocal[] = [];
    for (const v of scope.variables) {
      locals.push({ name: v.name, typeText: v.typeText, kind: 'variable' });
    }
    for (const c of scope.cursors) {
      locals.push({ name: c.name, typeText: 'cursor', kind: 'cursor' });
    }
    for (const t of scope.tableVariables) {
      locals.push({
        name: t.name,
        typeText: 'table variable',
        kind: 'tableVariable',
        columnCount: t.columns.length,
      });
    }

    const params = pickEnclosingRoutineParams(
      offset,
      extractProcedureSignatures(parsed),
      extractFunctionSignatures(parsed),
    );
    const seen = new Set<string>();
    const routineParams: WorkerRoutineParam[] = [];
    for (const p of params) {
      const key = p.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      routineParams.push({ name: p.name, typeText: p.typeText, mode: p.mode });
    }

    return { locals, routineParams };
  } catch {
    // Bundle not yet loaded or parse failure — degrade gracefully. The DML-side
    // candidates (tables, columns, keywords) still flow.
    return EMPTY;
  }
}

/** Minimal signature shape shared by `ProcedureSignature` + `FunctionSignature`. */
interface RoutineSigLike {
  readonly params: readonly { name: string; typeText: string; mode: string }[];
  readonly bodyRange: { startOffset: number };
}

/** Pick the parameters of the routine that lexically encloses `offset`.
 *
 *  Consolidated here from `mssqlProceduralLocals.ts` (plan16 T2 de-dup): the
 *  inline fallback is now the only renderer-side consumer. Behaviorally
 *  identical to `parseWorkerScope.pickEnclosingRoutineParams` (the worker-side
 *  copy) — the two intentionally stay separate module instances because
 *  `parseWorkerScope.ts` statically imports `parseWorker.ts`, whose `onmessage`
 *  bootstrap would install on `window` if pulled onto the main thread (a
 *  worker-only module must never be imported by renderer code). T-SQL routines
 *  do not nest, so the enclosing routine is the one whose signature STARTS
 *  latest at or before the cursor. */
function pickEnclosingRoutineParams(
  offset: number,
  procs: readonly RoutineSigLike[],
  funcs: readonly RoutineSigLike[],
): readonly { name: string; typeText: string; mode: string }[] {
  let best: RoutineSigLike | null = null;
  for (const sig of [...procs, ...funcs]) {
    if (sig.bodyRange.startOffset > offset) continue;
    if (!best || sig.bodyRange.startOffset > best.bodyRange.startOffset) best = sig;
  }
  return best ? best.params : [];
}
